import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Eye, EyeOff, Star, LogOut, FileText, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    image_url: '',
    category: 'general',
    is_featured: false,
    is_published: false,
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [articlesRes, subscribersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/articles`, getAuthHeaders()),
        axios.get(`${API}/newsletter/subscribers`, getAuthHeaders()),
        axios.get(`${API}/contact/submissions`, getAuthHeaders()),
      ]);
      setArticles(articlesRes.data);
      setSubscribers(subscribersRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleOpenDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        description: article.description,
        content: article.content,
        image_url: article.image_url,
        category: article.category,
        is_featured: article.is_featured,
        is_published: article.is_published,
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        description: '',
        content: '',
        image_url: '',
        category: 'general',
        is_featured: false,
        is_published: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.content || !formData.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, formData, getAuthHeaders());
        toast.success('Article updated successfully');
      } else {
        await axios.post(`${API}/articles`, formData, getAuthHeaders());
        toast.success('Article created successfully');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/articles/${id}`, getAuthHeaders());
      toast.success('Article deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const togglePublish = async (article) => {
    try {
      await axios.put(`${API}/articles/${article.id}`, 
        { is_published: !article.is_published }, 
        getAuthHeaders()
      );
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const toggleFeatured = async (article) => {
    try {
      await axios.put(`${API}/articles/${article.id}`, 
        { is_featured: !article.is_featured }, 
        getAuthHeaders()
      );
      toast.success(article.is_featured ? 'Removed from featured' : 'Marked as featured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-[#86868B]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg font-['Outfit']">C</span>
            </div>
            <span className="text-xl font-semibold text-[#1D1D1F] font-['Outfit']">
              Admin Dashboard
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="articles">
          <TabsList className="bg-white rounded-xl p-1 mb-8 w-full md:w-auto inline-flex">
            <TabsTrigger value="articles" className="flex items-center gap-2 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white rounded-lg px-4 py-2">
              <FileText size={18} />
              Articles ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="flex items-center gap-2 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white rounded-lg px-4 py-2">
              <Mail size={18} />
              Subscribers ({subscribers.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white rounded-lg px-4 py-2">
              <MessageSquare size={18} />
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Articles Tab */}
          <TabsContent value="articles">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] font-['Outfit']">
                Manage Articles
              </h2>
              <button
                onClick={() => handleOpenDialog()}
                data-testid="create-article-btn"
                className="bg-[#007AFF] text-white hover:bg-[#0062CC] rounded-xl px-6 py-3 font-medium transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                New Article
              </button>
            </div>

            {articles.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#E5E5EA]">
                <FileText className="w-16 h-16 text-[#86868B] mx-auto mb-4" />
                <p className="text-[#86868B]">No articles yet. Create your first one!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="articles-list">
                {articles.map((article) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`article-item-${article.id}`}
                    className="bg-white rounded-2xl p-6 border border-[#E5E5EA] flex gap-6"
                  >
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-32 h-24 object-cover rounded-xl flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2">
                        <h3 className="text-lg font-medium text-[#1D1D1F] font-['Outfit'] truncate">
                          {article.title}
                        </h3>
                        {article.is_featured && (
                          <Star className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" />
                        )}
                      </div>
                      <p className="text-[#86868B] text-sm line-clamp-2 mb-3">
                        {article.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="px-3 py-1 bg-[#F5F5F7] rounded-full text-[#86868B] capitalize">
                          {article.category}
                        </span>
                        <span className={`px-3 py-1 rounded-full ${article.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {article.is_published ? 'Published' : 'Draft'}
                        </span>
                        <span className="text-[#86868B]">
                          {formatDate(article.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => toggleFeatured(article)}
                        data-testid={`toggle-featured-${article.id}`}
                        className={`p-2 rounded-lg transition-colors ${article.is_featured ? 'bg-yellow-100 text-yellow-600' : 'bg-[#F5F5F7] text-[#86868B] hover:text-yellow-600'}`}
                        title={article.is_featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        <Star size={18} />
                      </button>
                      <button
                        onClick={() => togglePublish(article)}
                        data-testid={`toggle-publish-${article.id}`}
                        className={`p-2 rounded-lg transition-colors ${article.is_published ? 'bg-green-100 text-green-600' : 'bg-[#F5F5F7] text-[#86868B] hover:text-green-600'}`}
                        title={article.is_published ? 'Unpublish' : 'Publish'}
                      >
                        {article.is_published ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        onClick={() => handleOpenDialog(article)}
                        data-testid={`edit-article-${article.id}`}
                        className="p-2 rounded-lg bg-[#F5F5F7] text-[#86868B] hover:text-[#007AFF] transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        data-testid={`delete-article-${article.id}`}
                        className="p-2 rounded-lg bg-[#F5F5F7] text-[#86868B] hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <h2 className="text-2xl font-semibold text-[#1D1D1F] font-['Outfit'] mb-6">
              Newsletter Subscribers
            </h2>
            {subscribers.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#E5E5EA]">
                <Mail className="w-16 h-16 text-[#86868B] mx-auto mb-4" />
                <p className="text-[#86868B]">No subscribers yet.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E5E5EA] overflow-hidden">
                <table className="w-full" data-testid="subscribers-table">
                  <thead className="bg-[#F5F5F7]">
                    <tr>
                      <th className="text-left px-6 py-4 text-[#86868B] font-medium">Email</th>
                      <th className="text-left px-6 py-4 text-[#86868B] font-medium">Subscribed On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="border-t border-[#E5E5EA]">
                        <td className="px-6 py-4 text-[#1D1D1F]">{sub.email}</td>
                        <td className="px-6 py-4 text-[#86868B]">{formatDate(sub.subscribed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <h2 className="text-2xl font-semibold text-[#1D1D1F] font-['Outfit'] mb-6">
              Contact Messages
            </h2>
            {contacts.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#E5E5EA]">
                <MessageSquare className="w-16 h-16 text-[#86868B] mx-auto mb-4" />
                <p className="text-[#86868B]">No messages yet.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="contacts-list">
                {contacts.map((contact) => (
                  <div key={contact.id} className="bg-white rounded-2xl p-6 border border-[#E5E5EA]">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-[#1D1D1F]">{contact.name}</h3>
                        <p className="text-[#86868B] text-sm">{contact.email}</p>
                        {contact.company && (
                          <p className="text-[#86868B] text-sm">{contact.company}</p>
                        )}
                      </div>
                      <span className="text-[#86868B] text-sm">{formatDate(contact.submitted_at)}</span>
                    </div>
                    <p className="text-[#1D1D1F] bg-[#F5F5F7] rounded-xl p-4">{contact.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Article Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingArticle ? 'Edit Article' : 'Create New Article'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="article-form">
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="article-title-input"
                className="w-full bg-[#F5F5F7] border-none rounded-xl px-4 py-3 text-[#1D1D1F] focus:ring-2 focus:ring-[#007AFF]"
                placeholder="Article title"
              />
            </div>
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="article-description-input"
                rows={2}
                className="w-full bg-[#F5F5F7] border-none rounded-xl px-4 py-3 text-[#1D1D1F] focus:ring-2 focus:ring-[#007AFF] resize-none"
                placeholder="Short description"
              />
            </div>
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                data-testid="article-content-input"
                rows={6}
                className="w-full bg-[#F5F5F7] border-none rounded-xl px-4 py-3 text-[#1D1D1F] focus:ring-2 focus:ring-[#007AFF] resize-none"
                placeholder="Full article content"
              />
            </div>
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">Image URL *</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                data-testid="article-image-input"
                className="w-full bg-[#F5F5F7] border-none rounded-xl px-4 py-3 text-[#1D1D1F] focus:ring-2 focus:ring-[#007AFF]"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                data-testid="article-category-select"
                className="w-full bg-[#F5F5F7] border-none rounded-xl px-4 py-3 text-[#1D1D1F] focus:ring-2 focus:ring-[#007AFF]"
              >
                <option value="general">General</option>
                <option value="insurance">Insurance</option>
                <option value="loans">Loans</option>
                <option value="finance">Finance</option>
              </select>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  data-testid="article-published-checkbox"
                  className="w-5 h-5 rounded accent-[#007AFF]"
                />
                <span className="text-[#1D1D1F]">Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  data-testid="article-featured-checkbox"
                  className="w-5 h-5 rounded accent-[#007AFF]"
                />
                <span className="text-[#1D1D1F]">Featured</span>
              </label>
            </div>
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                className="px-6 py-3 text-[#86868B] hover:text-[#1D1D1F] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="article-submit-btn"
                className="bg-[#007AFF] text-white hover:bg-[#0062CC] rounded-xl px-6 py-3 font-medium transition-all"
              >
                {editingArticle ? 'Update Article' : 'Create Article'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
